import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { initMenu, changeActiveMenuFromLocation } from '../redux/actions';

const MenuItemWithChildren = ({
    item,
    linkClassNames,
    subMenuClassNames,
    activatedMenuItemIds,
    menuItemClick,
    subMenuItemClick,
    user,
}) => (
    <li
        className={classNames('side-nav-item', {
            // 'mm-active': activatedMenuItemIds.indexOf(item.id) >= 0,
            active: activatedMenuItemIds.indexOf(item.id) >= 0,
        })}>
        <Link
            to="#"
            onClick={(e) => {
                e.preventDefault();
                menuItemClick(item);
            }}
            className={classNames(
                'side-sub-nav-link d-flex align-items-center justify-content-between',
                linkClassNames
            )}
            aria-expanded={activatedMenuItemIds.indexOf(item.id) >= 0}>
            <div className="d-flex align-items-center">
                {item.icon && <i className={classNames(item.icon, 'me-2')}></i>}
                <span>{item.name}</span>
            </div>
            <i
                className={classNames('chevron-icon mdi mdi-chevron-right transition-transform', {
                    'rotate-90': activatedMenuItemIds.includes(item.id),
                })}
            />
        </Link>

        <ul
            className={classNames(subMenuClassNames, 'mm-collapse', {
                'mm-collapsed mm-show': activatedMenuItemIds.indexOf(item.id) >= 0,
            })}
            aria-expanded={activatedMenuItemIds.indexOf(item.id) >= 0}>
            {item.children.map((child, i) => (
                <React.Fragment key={i}>
                    {child.children ? (
                        child.excludeState && user?.isBlackListState ? (
                            <></>
                        ) : (
                            <MenuItemWithChildren
                                item={child}
                                user={user}
                                linkClassNames=""
                                activatedMenuItemIds={activatedMenuItemIds}
                                subMenuClassNames="side-nav-third-level"
                            />
                        )
                    ) : child.excludeState && user?.isBlackListState ? (
                        <></>
                    ) : (
                        <MenuItem
                            item={child}
                            className={classNames({ 'mm-active': activatedMenuItemIds.indexOf(child.id) >= 0 })}
                            linkClassName=""
                            isActive={activatedMenuItemIds.indexOf(child.id) >= 0}
                            menuItemClick={subMenuItemClick}
                        />
                    )}
                </React.Fragment>
            ))}
        </ul>
    </li>
);

const MenuItem = ({ item, className, linkClassName, isActive, menuItemClick }) => (
    <li className={classNames('side-nav-item', className)}>
        <MenuItemLink item={item} className={linkClassName} isActive={isActive} menuItemClick={menuItemClick} />
    </li>
);

const MenuItemLink = ({ item, className, isActive, menuItemClick, user }) => {
    // Special logic for Manage Bank Accounts
    let path = item.path;
    if (item.name === 'Manage Bank Accounts') {
        path = '/bank/accounts';
    }
    return (
        <Link
            to={path}
            className={classNames('side-nav-link-ref', 'side-sub-nav-link', className)}
            onClick={(e) => {
                if (isActive) e.preventDefault();
                else menuItemClick(item);
            }}>
            {item.icon && <i className={item.icon}></i>}
            {item.badge && <span className={`badge badge-${item.badge.variant} float-right`}>{item.badge.text}</span>}
            <span> {item.name} </span>
        </Link>
    );
};

const findActiveItemByPath = (items, path) => {
    for (const item of items) {
        if (item.path === path) return item;
        if (item.children) {
            const match = findActiveItemByPath(item.children, path);
            if (match) return match;
        }
    }
    return null;
};

const findParentIds = (items, targetId, parents = []) => {
    for (const item of items) {
        if (item.id === targetId) {
            return parents;
        }
        if (item.children) {
            const found = findParentIds(item.children, targetId, [...parents, item.id]);
            if (found.length) return found;
        }
    }
    return [];
};

const AppMenu = ({ initMenu, changeActiveMenuFromLocation, menu, mode = 'vertical', user }) => {
    const [activatedMenuItemIds, setActivatedMenuItemIds] = useState([]);
    const [mainMenuId, setMainMenuId] = useState(0);
    const location = useLocation();

    useEffect(() => {
        if (!menu.menuItems) {
            initMenu();
        } else {
            const activeItem = findActiveItemByPath(menu.menuItems, location.pathname);
            if (activeItem) {
                const parentIds = findParentIds(menu.menuItems, activeItem.id);
                setActivatedMenuItemIds([...parentIds, activeItem.id]);
            } else {
                setActivatedMenuItemIds([]);
            }
        }

        changeActiveMenuFromLocation();
    }, [menu.menuItems, location.pathname]);

    const mainMenuClick = (item) => {
        setMainMenuId(item.id);

        setActivatedMenuItemIds((prevIds) => (prevIds.includes(item.id) ? [] : [item.id]));
    };

    const subMenuItemClick = (item) => {
        setActivatedMenuItemIds([mainMenuId, item.id]);
    };

    const isHorizontal = mode === 'horizontal';

    let menuItems = menu && menu.menuItems ? menu.menuItems : [];
    const defaultDisplayedItems = window.screen.width > 1366 ? 7 : 5;

    if (isHorizontal && menuItems.length > defaultDisplayedItems) {
        const displayedItems = menuItems.slice(0, defaultDisplayedItems);
        const otherItems = {
            id: menuItems.length + 1,
            path: '/',
            name: 'More',
            icon: 'uil-ellipsis-h',
            children: menuItems.slice(7, menuItems.length),
        };
        menuItems = [...displayedItems, otherItems];
    }

    return (
        <React.Fragment>
            <div className={classNames({ 'topbar-nav': isHorizontal })}>
                {menu && menuItems && menuItems.length ? (
                    <ul className="metismenu side-nav" id="menu-bar">
                        {menuItems.map((item, i) => (
                            <React.Fragment key={item.id}>
                                <React.Fragment>
                                    {item.header && !isHorizontal && (
                                        <li className="side-nav-title side-nav-item" key={i + '-el'}>
                                            {item.header}
                                        </li>
                                    )}
                                    {item.children ? (
                                        item.excludeState && user?.isBlackListState ? (
                                            <></>
                                        ) : (
                                            <MenuItemWithChildren
                                                item={item}
                                                user={user}
                                                subMenuClassNames="side-nav-second-level"
                                                activatedMenuItemIds={activatedMenuItemIds}
                                                linkClassNames="side-nav-link"
                                                menuItemClick={mainMenuClick}
                                                subMenuItemClick={subMenuItemClick}
                                            />
                                        )
                                    ) : item.excludeState && user?.isBlackListState ? (
                                        <></>
                                    ) : (
                                        <MenuItem
                                            item={item}
                                            className={classNames({
                                                'mm-active': activatedMenuItemIds.indexOf(item.id) >= 0,
                                            })}
                                            linkClassName="side-nav-link"
                                            menuItemClick={subMenuItemClick}
                                        />
                                    )}
                                </React.Fragment>
                            </React.Fragment>
                        ))}
                    </ul>
                ) : null}
            </div>
        </React.Fragment>
    );
};

AppMenu.propTypes = {
    initMenu: PropTypes.func.isRequired,
    changeActiveMenuFromLocation: PropTypes.func.isRequired,
    menu: PropTypes.object.isRequired,
    mode: PropTypes.string,
    user: PropTypes.object,
};

const mapStateToProps = (state) => ({
    menu: state.AppMenu,
    user: state.Auth.user,
});

export default connect(mapStateToProps, { initMenu, changeActiveMenuFromLocation })(AppMenu);
